const ORDERS_KEY = "foodoor_scheduled_orders";
const PLAYED_KEY = "foodoor_scheduled_played";
const LAST_ALERT_KEY = "foodoor_scheduled_alert_last";
const ANNOUNCED_KEY = "foodoor_scheduled_announced";

export const NEW_SCHEDULED_ORDER_TEXT = "You have a new scheduled order to handle";

// Once at 60 minutes before the slot, then every 5 minutes from 45 down to the
// slot itself, while the order still needs a rider or the restaurant's answer.
export const REMINDER_MARKS = [60, 45, 40, 35, 30, 25, 20, 15, 10, 5, 0];
const AFTER_START_GRACE_MINUTES = 5;
export const DEDUPE_WINDOW_MS = 90 * 1000;
const PRUNE_AFTER_MS = 24 * 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

export const getStorage = () => {
  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

const readJson = (storage, key) => {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const writeJson = (storage, key, value) => {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // storage full or blocked: reminders still ring, just without cross-tab memory
  }
};

export const slotStartMs = (scheduledDate, fromTime) => {
  if (!scheduledDate || !fromTime) return null;
  const ms = Date.parse(`${scheduledDate}T${fromTime}+05:30`);
  return Number.isNaN(ms) ? null : ms;
};

const slotStartFromIso = (slotStart) => {
  if (typeof slotStart !== "string" || !slotStart.includes("T")) return null;
  const [day, clock] = slotStart.split("T");
  return slotStartMs(day, clock);
};

export const needsAttention = (row) =>
  !(Boolean(row.restaurant_accepted) && (Boolean(row.rider_assigned) || Boolean(row.self_pickup)));

export const rowsFromSnapshot = (scheduledData) => {
  if (!Array.isArray(scheduledData)) return null;
  return scheduledData
    .filter((row) => row && row.id !== undefined && row.id !== null)
    .map((row) => ({ id: row.id, slotStart: slotStartFromIso(row.slot_start), needsAttention: needsAttention(row) }));
};

export const playedKey = (orderId, minutes) => `${orderId}:${minutes}`;

export const dueReminders = (orders, played, nowMs) => {
  const orderIds = [];
  const keys = [];
  orders.forEach(({ id, slotStart, needsAttention: open }) => {
    if (open === false || slotStart === null || slotStart === undefined) return;
    const minutes = (slotStart - nowMs) / MINUTE_MS;
    if (minutes > REMINDER_MARKS[0] || minutes < -AFTER_START_GRACE_MINUTES) return;
    const crossed = REMINDER_MARKS.filter((mark) => minutes <= mark && !played[playedKey(id, mark)]);
    if (!crossed.length) return;
    orderIds.push(id);
    crossed.forEach((mark) => keys.push(playedKey(id, mark)));
  });
  return { orderIds, keys };
};

export const prunePlayed = (played, orders, nowMs) => {
  const startById = {};
  orders.forEach(({ id, slotStart }) => {
    startById[id] = slotStart;
  });
  const kept = {};
  Object.keys(played).forEach((key) => {
    const start = startById[key.split(":")[0]];
    const stamp = played[key];
    const known = start !== undefined && start !== null;
    const slotPassed = known && nowMs - start > PRUNE_AFTER_MS;
    const stale = !known && nowMs - stamp > PRUNE_AFTER_MS;
    if (!slotPassed && !stale) kept[key] = stamp;
  });
  return kept;
};

export const loadStoredOrders = (storage) => {
  const stored = storage && readJson(storage, ORDERS_KEY);
  return stored && Array.isArray(stored.orders) ? stored.orders : [];
};

export const loadSeeded = (storage) => {
  const stored = storage && readJson(storage, ORDERS_KEY);
  return Boolean(stored && stored.seeded === true);
};

export const saveOrders = (storage, orders, nowMs, seeded = false) => {
  if (storage) writeJson(storage, ORDERS_KEY, { orders, fetchedAt: nowMs, seeded });
};

export const loadPlayed = (storage) => {
  const stored = storage && readJson(storage, PLAYED_KEY);
  return stored && typeof stored === "object" && !Array.isArray(stored) ? stored : {};
};

export const claimDueReminders = (storage, orders, nowMs) => {
  const played = loadPlayed(storage);
  const { orderIds, keys } = dueReminders(orders, played, nowMs);
  if (!orderIds.length) return [];
  keys.forEach((key) => {
    played[key] = nowMs;
  });
  if (storage) writeJson(storage, PLAYED_KEY, prunePlayed(played, orders, nowMs));
  return orderIds;
};

export const loadAnnounced = (storage) => {
  const stored = storage && readJson(storage, ANNOUNCED_KEY);
  return stored && typeof stored === "object" && !Array.isArray(stored) ? stored : {};
};

const claimAnnouncements = (storage, ids, nowMs) => {
  const announced = loadAnnounced(storage);
  const mine = ids.filter((id) => !announced[id]);
  mine.forEach((id) => {
    announced[id] = nowMs;
  });
  const kept = {};
  Object.keys(announced).forEach((id) => {
    if (nowMs - announced[id] <= PRUNE_AFTER_MS) kept[id] = announced[id];
  });
  if (storage) writeJson(storage, ANNOUNCED_KEY, kept);
  return mine;
};

// The websocket snapshot carries every open scheduled order with its slot start
// and whether it still needs attention, so nothing is ever fetched: a new id is
// announced once, the rest is stored for the reminder timer.
export const syncScheduledOrders = ({ storage, rows, announce, nowMs }) => {
  const parsed = rowsFromSnapshot(rows);
  if (!parsed) return { ignored: true };
  if (!loadSeeded(storage)) {
    saveOrders(storage, parsed, nowMs, true);
    return { seeded: true, added: [], announced: [] };
  }
  const known = new Set(loadStoredOrders(storage).map((order) => order.id));
  const added = parsed.map((row) => row.id).filter((id) => !known.has(id));
  const toAnnounce = claimAnnouncements(storage, added, nowMs);
  saveOrders(storage, parsed, nowMs, true);
  if (toAnnounce.length) announce();
  return { seeded: true, added, announced: toAnnounce };
};

export const shouldPlayScheduledAlert = (lastAt, nowMs) =>
  !(typeof lastAt === "number" && nowMs - lastAt >= 0 && nowMs - lastAt < DEDUPE_WINDOW_MS);

export const playScheduledAlert = (nowMs = Date.now()) => {
  const storage = getStorage();
  const last = storage ? readJson(storage, LAST_ALERT_KEY) : null;
  if (!shouldPlayScheduledAlert(last, nowMs)) return false;
  if (storage) writeJson(storage, LAST_ALERT_KEY, nowMs);
  new Audio("/sounds/scheduled_alert.mp3")
    .play()
    .catch((err) => console.log("Audio play failed:", err));
  return true;
};

export const announceNewScheduledOrder = () => {
  const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
  const Utterance = typeof window !== "undefined" ? window.SpeechSynthesisUtterance : null;
  if (synth && typeof synth.speak === "function" && typeof Utterance === "function") {
    try {
      synth.speak(new Utterance(NEW_SCHEDULED_ORDER_TEXT));
      return "spoken";
    } catch (error) {
      // no voice available: fall through to the tone
    }
  }
  new Audio("/sounds/scheduled_alert.mp3")
    .play()
    .catch((err) => console.log("Audio play failed:", err));
  return "tone";
};
